import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Clock, Users, QrCode, ShieldCheck, HardHat, FileText, BarChart3, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";

const MODULE_KEY = "TIME_TRACKING";

export default function TimeUpgrade() {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const activate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/employer/module/${MODULE_KEY}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setBusy(false); }
  };
  const features = [
    { icon: Users, title: t("timeUp.f1t"), desc: t("timeUp.f1d") },
    { icon: QrCode, title: t("timeUp.f2t"), desc: t("timeUp.f2d") },
    { icon: ShieldCheck, title: t("timeUp.f3t"), desc: t("timeUp.f3d") },
    { icon: HardHat, title: t("timeUp.f4t"), desc: t("timeUp.f4d") },
    { icon: FileText, title: t("timeUp.f5t"), desc: t("timeUp.f5d") },
    { icon: BarChart3, title: t("timeUp.f6t"), desc: t("timeUp.f6d") },
  ];
  return (
    <div data-testid="time-upgrade" className="mx-auto max-w-5xl">
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-navy via-navy to-[#0d2440] p-8 text-white sm:p-12">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-cyan/20 px-3 py-1 text-xs font-bold uppercase tracking-wider text-cyan"><Clock className="h-3.5 w-3.5" /> {t("timeUp.badge")}</span>
        <h1 className="mt-5 font-heading text-4xl font-extrabold leading-tight sm:text-5xl">{t("timeUp.title")}</h1>
        <p className="mt-4 max-w-2xl text-base text-slate-300">{t("timeUp.subtitle")}</p>
        <div className="mt-8 flex flex-wrap items-end gap-6">
          <div>
            <div className="flex items-end gap-1"><span className="font-heading text-5xl font-extrabold">{t("timeUp.price")}</span><span className="mb-1.5 text-sm text-slate-300">{t("timeUp.priceUnit")}</span></div>
            <p className="mt-1 text-xs text-slate-400">{t("timeUp.priceNote")}</p>
          </div>
          <Button onClick={activate} disabled={busy} data-testid="activate-time-module-btn" className="h-12 rounded-full bg-cyan px-8 text-base font-bold text-white hover:bg-cyan-dark">
            {busy ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> {t("timeUp.activating")}</> : t("timeUp.activate")}
          </Button>
        </div>
      </div>
      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {features.map((f, i) => (
          <div key={i} className="card-soft flex gap-4 p-6" data-testid={`time-feature-${i}`}>
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-cyan/10 text-cyan-dark"><f.icon className="h-5 w-5" /></div>
            <div><h3 className="font-heading font-bold text-navy">{f.title}</h3><p className="mt-1 text-sm text-slate-500">{f.desc}</p></div>
          </div>
        ))}
      </div>
      <p className="mt-6 text-center text-xs text-slate-400">{t("timeUp.legalNote")}</p>
    </div>
  );
}
